import { useWindowSize } from "@/src/sb/hooks/useWindowSize"
import { Feature, Point } from "geojson"
import { FeatureCollection } from "geojson"
import { LatLng, geoJSON } from "leaflet"
import { useRouter } from "next/router"
import { ReactElement, useEffect, useRef, useState } from "react"
import { MapContainer } from "react-leaflet"

import {
  ELECTION_NO_RESULT_COLOR,
  ELECTION_PARTY_COLOR_OTHERS
} from "../../business-layer/constants"
import {
  idFromGeoJSONProperties,
  titleFromGeoJSONProperties
} from "../../business-layer/selectors"
import { GeoJSONFeatureProperties } from "../../business-layer/types"
import Typography from "../Typography"
import { GeoJSONMap, GeoJSONMapProps } from "./GeoJSONMap"
import { ZOOM_DELTA, ZoomControl } from "./ZoomControl"

const INITIAL_ZOOM = {
  sm: 5.25,
  md: 6
}

const DEFAULT_LEGEND = [
  { name: "APC", color: "#60A5FA" },
  { name: "LP", color: "#F05042" },
  { name: "PDP", color: "#109373" },
  { name: "NNPP", color: "#264ABC" },
  { name: "Others", color: ELECTION_PARTY_COLOR_OTHERS },
  { name: "Awaiting", color: ELECTION_NO_RESULT_COLOR }
]

type PopoverRenderProps = any

export type MapProps = {
  data: FeatureCollection
  stateBoundaries?: FeatureCollection
  interactiveRegions?: string[]
  highlightOnHover?: "fill" | "opacity"
  palette?: GeoJSONMapProps["palette"]
  className?: string
  showTitles?: boolean
  initialSelectedFeatureId?: string
  avoidCollisions?: boolean
  legend?: Array<{ name: string; color: string }>
  selectRegionColor?: (
    properties: GeoJSONFeatureProperties
  ) => string | undefined
  idFromProperties?: (properties: GeoJSONFeatureProperties) => string
  titleFromProperties?: (properties: GeoJSONFeatureProperties) => string
  onClickMapFeature?: (properties: GeoJSONFeatureProperties) => void
  renderPopover?: (renderProps: PopoverRenderProps) => ReactElement
}

function Map({
  data,
  className: extraClassNames = "",
  interactiveRegions = [],
  showTitles,
  highlightOnHover,
  avoidCollisions,
  palette,
  stateBoundaries,
  initialSelectedFeatureId,
  onClickMapFeature,
  selectRegionColor,
  legend = DEFAULT_LEGEND,
  idFromProperties = idFromGeoJSONProperties,
  titleFromProperties = titleFromGeoJSONProperties,
  renderPopover
}: MapProps) {
  const mapRef = useRef<null | HTMLDivElement>(null)
  const mapTopBorderline = useRef<HTMLDivElement | null>(null)
  const mapBottomBorderline = useRef<HTMLDivElement | null>(null)
  const [mobile] = useWindowSize()
  const { query } = useRouter()
  const [selectedFeature, setSelectedFeature] = useState<
    Feature<Point, GeoJSONFeatureProperties> | undefined
  >(undefined)

  const scrollMapToView = () => {
    // additional 8px spacing
    const marginTolerance = 8
    const mapBorderLineScrollMargin = mobile
      ? window.innerHeight - 227 - marginTolerance
      : 81 + marginTolerance
    const borderLineClass = `${mapBorderLineScrollMargin}px`

    let mapBorderLine

    if (mobile) {
      mapBorderLine = mapBottomBorderline.current
    } else {
      mapBorderLine = mapTopBorderline.current
    }

    if (mapBorderLine) {
      mapBorderLine.style.scrollMarginTop = borderLineClass
      mapBorderLine.scrollIntoView()
    }
  }

  const onSelectFeature = (f: Feature<Point, GeoJSONFeatureProperties>) => {
    scrollMapToView()
    onClickMapFeature?.(f.properties)
    setSelectedFeature(f)
  }

  const zoom = mobile ? INITIAL_ZOOM.sm : INITIAL_ZOOM.md

  useEffect(() => {
    if (query?.state || query?.constituency) {
      scrollMapToView()
    }
  }, [query])

  useEffect(() => {
    const selected: any =
      initialSelectedFeatureId &&
      data.features.find(
        ({ properties: p }) =>
          initialSelectedFeatureId ===
          idFromGeoJSONProperties(p as GeoJSONFeatureProperties)
      )
    setSelectedFeature(selected)
  }, [initialSelectedFeatureId])

  if (data.features.length === 0) {
    return null
  }

  const center: LatLng = geoJSON(data).getBounds().getCenter()

  /* On attribution: https://gis.stackexchange.com/questions/192088/how-to-remove-attribution-in-leaflet/192120#192120 */
  return (
    <div className={`md:relative ${extraClassNames}`}>
      {selectedFeature &&
        renderPopover?.({
          properties: selectedFeature.properties,
          onClose: () => setSelectedFeature(undefined)
        })}
      <article className="border border-gray-10 mb-8">
        <section className="py-2 px-4 flex flex-row flex-wrap justify-between sm:justify-start">
          {legend.map(({ name, color }) => (
            <article
              key={name}
              className="flex flex-row first:ml-0 sm:ml-10 my-2"
            >
              <div className="w-5 h-5" style={{ backgroundColor: color }} />
              <Typography type="p15x" className="ml-2 my-auto text-gray-800">
                {name}
              </Typography>
            </article>
          ))}
        </section>
      </article>

      <div ref={mapTopBorderline} />

      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={false}
        attributionControl={false}
        className={"w-full h-[325px] sm:h-[525px] mx-auto p bg-white"}
        zoomControl={false}
        doubleClickZoom={false}
        zoomSnap={ZOOM_DELTA}
        touchZoom={true}
      >
        <ZoomControl />
        <GeoJSONMap
          ref={mapRef}
          palette={palette}
          data={data}
          showTitles={showTitles}
          highlightOnHover={highlightOnHover}
          avoidCollisions={avoidCollisions}
          stateBoundaries={stateBoundaries}
          interactiveRegions={interactiveRegions}
          selectedFeature={selectedFeature}
          selectRegionColor={selectRegionColor}
          onSelectFeature={onSelectFeature}
          idFromProperties={idFromProperties}
          titleFromProperties={titleFromProperties}
        />
      </MapContainer>

      <div ref={mapBottomBorderline} />
    </div>
  )
}

export default Map
